'use client';

import Image from 'next/image';
import type { ReactNode } from 'react';
import type { FieldValues } from 'react-hook-form';
import type { InputProps } from '../types';
import { getErrorMessage } from '../utils/getErrorMessage';
import { inputStyles, labelStyles } from './styles';

export const Input = <T extends FieldValues>({
  label,
  name,
  errors,
  endIcon,
  startIcon,
  size,
  labelStyle,
  otpError,
  className,
  required,
  placeholderColor,
  helperText,
  suffix,
  ...rest
}: InputProps<T> & { required?: boolean; helperText?: string; isDraft?: boolean; suffix?: ReactNode }) => {
  const errorMessage = getErrorMessage<T>(errors, name);
  const intent = rest.intent;

  return (
    <div className={className}>
      {label && (
        <label className={labelStyles({ intent: labelStyle?.intent, required })} htmlFor={name}>
          {label}
        </label>
      )}
      <div className="relative w-full flex items-center">
        {intent === 'search' && !startIcon && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
            <Image src="/icons/search.svg" width={16} height={16} alt="Search" />
          </div>
        )}
        {intent === 'searchLarge' && !startIcon && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
            <Image src="/icons/search.svg" width={24} height={24} alt="Search" />
          </div>
        )}
        {startIcon && <div className="absolute left-3 top-1/2 -translate-y-1/2">{startIcon}</div>}

        <input
          name={name}
          className={inputStyles({
            intent,
            placeholderColor,
            size,
            disabled: rest.disabled,
            error: !!errorMessage,
            otpError,
            hasStartIcon: !!startIcon,
            hasEndIcon: !!endIcon,
          })}
          required={required}
          {...rest}
        />

        {suffix && (
          <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">
            <div className="flex items-center justify-center h-[24px] min-w-[32px] rounded-[6px] bg-grey-200 px-2">
              <span className="text-body-regular text-grey-700">{suffix}</span>
            </div>
          </div>
        )}

        {endIcon && <div className="absolute right-4 top-1/2 -translate-y-1/2">{endIcon}</div>}
      </div>
      {errorMessage && <p className="text-red-500 text-body-small mt-[6px]">{errorMessage}</p>}
      {helperText && <p className="text-blue-300 text-body-small mt-[6px]">{helperText}</p>}
    </div>
  );
};
